/**
 * @file       Inspection service
 * @version    2.0.0
 * @author     Mehran Majidi
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Observable, map, catchError, of } 
    from 'rxjs';

import { SecureModule } from "@app/secure/secure.module";

import { ERROR } from '@app/constants/Error.constant';
import { Inspection } from '@app/secure/interface/Inspection.interface';
import { TASK } from '@app/constants/Api.constant';
import { CASE_INSPECTION } from '@app/constants/Api.constant';
import { ApiService } from '@app/secure/service/Api.service';

//this service must be in root because it is used during
//login
@Injectable
({
    providedIn: 'root'
})
class InspectionService implements OnDestroy
{
    // inspection subscription
    subscriptions = [];

    inspection$: ReplaySubject<Inspection> = new ReplaySubject<Inspection>(1);
    
    /*
        new ReplaySubject<Inspection> 
        ({
            number: null,
            typeREF: null,
            statusREF: null,
            totalAmount: null,
            totalPaid: null,
            balance: null,
            customerUId: null,
            customerNumber: null,
            customerFirstName: null,
            customerLastName: null,
            officerUId: null,
            officerNumber: null,
            officerFirstName: null,
            officerLastName: null,
            startDate: null,
            startTime: null,
            endDate: null,
            endTime: null,
            duration: null});
 */   
    // selected inspection as observable
    // selectedInspection$ = new ReplaySubject<Inspection>(1);
    
    constructor
    (
        private apiService: ApiService
    ) 
    {
        let cyData;
        let subscription;
        
        if (localStorage.getItem('activeInspectionNumber'))
        {            
            subscription = this.getInspection(
                        localStorage.getItem('activeInspectionNumber'))
            .subscribe(result =>                         
            {
                cyData = result.cyData;
                
                this.inspection$.next
                ({
                    number: localStorage.getItem('activeInspectionNumber'),
                    typeREF: cyData.typeREF,
                    statusREF: cyData.statusREF,
                    totalAmount: cyData.totalAmount,
                    totalPaid: cyData.totalPaid,
                    balance: cyData.balance,
                    customerUId: cyData.customerUId,
                    customerNumber: cyData.customerNumber,
                    customerName: cyData.customerName,
                    customerFirstName: cyData.customerFirstName,
                    customerLastName: cyData.customerLastName,
                    officerUId: cyData.officerUId,
                    officerNumber: cyData.officerNumber,
                    officerFirstName: cyData.officerFirstName,
                    officerLastName: cyData.officerLastName,
                    startDate: cyData.startDate,
                    startTime: cyData.startTime,
                    //endDate: cyData.endDate,
                    //endTime: cyData.endTime,
                    duration: cyData.duration        
                });                
            },
            error =>
            {
              console.log(error);
            });
            
            this.subscriptions.push(subscription);
            
//            this.selectedInspection$ = this.inspection$;            
        }
        else
        {
            this.inspection$.next
            ({
                number: null,
                typeREF: null,
                statusREF: null,
                totalAmount: null,
                totalPaid: null,
                balance: null,
                customerUId: null,
                customerNumber: null,
                customerName: null,
                customerFirstName: null,
                customerLastName: null,
                officerUId: null,
                officerNumber: null,
                officerFirstName: null,
                officerLastName: null,
                startDate: null,
                startTime: null,
                //endDate: null,
                //endTime: null,
                duration: null        
            });
        }
    }
    
    private getInspection
    (
        inspectionNumber
    )
    {
        let data;
        let requestObj; 
        
        try
        {
            data = 
            {
                taskNumber: inspectionNumber
            };
            
            requestObj = this.apiService.postRaw<any>(
                'task/tasksInfoByNumber',
                data
            );
/*            
            if (response.error == CONSTANTS.ERROR.NO_ERROR)
            {
                console.log('no error');
            }
*/            
        }        
        catch(e)
        {
            console.log(e);
        }

        return requestObj;        
    }

    ngOnDestroy ()
    {
        let i;
        
        for (i=0;i<this.subscriptions.length;i++)
        {
            this.subscriptions[i].unsubscribe();
        }
        
        // reset the subscriptions
        this.subscriptions = [];
    }
    
    setInspection
    (
        inspection: Inspection
    )
    {
        if (inspection)
        {
            if (inspection.number == null)
            {
                localStorage.removeItem('activeInspectionNumber');
            }
            else
            {
                localStorage.setItem('activeInspectionNumber',
                                     inspection.number);
            }
        }
        else
        {
            localStorage.removeItem('activeInspectionNumber');    
        }
        this.inspection$.next(inspection);
    }

    updateInspection
    (
        newInspection: Inspection
    ) 
    {
        this.inspection$.next(newInspection);
    }

    public updateInspectionBalance
    (
        newBalance: number,
        newTotalAmount: number,
        newTotalPaid: number,
        activeInspection: any
    ): Observable<any>
    {
        let serviceObj: any;
        let taskAction: any;

        let postData = 
        {
            taskData: 
            {
                number: activeInspection.number,
                balance: newBalance,
                totalAmount: newTotalAmount ?? null,
                totalPaid: newTotalPaid ?? null
            }
        };
  
        serviceObj = this.apiService.postRaw<any>(
            TASK.UPDATE_TASK_BALANCE,
            postData
        )
        .pipe
        (
            map((result: any) => 
            {
                taskAction = 
                {
                    done: false,
                    error: '',
                };

                if (result && result.error === ERROR.NO_ERROR) 
                {
                    taskAction.done = true;
                } 
                else 
                {
                    taskAction.error = ERROR.UPDATE_FAILED;
                }

                return taskAction;
            }),
            catchError(err => 
            {
                console.log(err);
                taskAction = 
                {
                    done: false,
                    error: '',
                };
                try 
                {
                    const ERROR_CODE = JSON.parse(err.message);

                    switch (ERROR_CODE.error) 
                    {
                        case ERROR.INVALID_POST_DATA:
                             taskAction.error = "Invalid post data";
                             break;
                        case ERROR.GENERAL:
                             taskAction.error = "General error";
                             break;
                        case ERROR.UPDATE_FAILED:
                             taskAction.error = "Update failed";
                             break;
                        default:
                             taskAction.error = "Unknown error";
                             break;
                        // end switch
                    }
                } 
                catch (e) 
                {
                    taskAction.error = "Error parsing error message";
                }

                return of(taskAction);
            })
        );

        return serviceObj;
    }

    /**
     * @desc   - validates the inspection number
     * @access - public
     * @param  - string inspectionNumber: the number of the inspection
     * @return - Observable<any>
     */
    public validateInspectionNumber
    (
        inspectionNumber: string
    )
    {
        let response: any
        let serviceObj: any;

        let postData =
        {
            taskNumber: inspectionNumber
        }

        serviceObj = this.apiService
                       .postRaw<any>(
                           TASK.VALIDATE_TASK_NUMBER,
                           postData
                       )
        .pipe(map((result: any) => 
        {
            response = 
            {
                available: false,
                error: '',
            };

            if (result.cyData?.available && result.error === ERROR.NO_ERROR)
            {
                response.available = true;
            }

            return response;
        }),
        catchError(err =>
        {
            console.log(err);
            response =
            {
                available: false,
                error: '',
            };

            try
            {
                const ERROR_CODE = JSON.parse(err.message);
                switch (ERROR_CODE.error)
                {
                    case ERROR.INVALID_POST_DATA:
                        response.error = "Invalid post data";
                        break;
                    default:
                        response.error = "General Error";
                        break;
                }
            }
            catch (e)
            {
                response.error = "Error parsing error message";
            }

            return of(response);
        }));

        return serviceObj;
    }

    /**
     * @desc   - validates the inspection case numbers
     * @access - public
     * @param  - number caseNumberFrom: Starting case number
     * @param  - number caseNumberTo: The last case number
     * @return - Observable<any>
     */
    public validateCaseNumbers
    (
        caseNumberFrom: number,
        caseNumberTo: number
    )
    {
        let response: any
        let serviceObj: any;

        let postData =
        {
            caseNumberFrom: caseNumberFrom,
            caseNumberTo: caseNumberTo
        }

        serviceObj = this.apiService
                         .postRaw<any>(
                             CASE_INSPECTION.VALIDATE_CASE_NUMBERS,
                             postData
                         )
        .pipe(map((result: any) => 
        {
            response = 
            {
                available: false,
                error: '',
            };

            if (result.cyData?.available && result.error === ERROR.NO_ERROR)
            {
                response.available = true;
            }
            else
            {
                response.error = result.error;
            }

            return response;
        }),
        catchError(err =>
        {
            console.log(err);
            response =
            {
                available: false,
                error: '',
            };

            try
            {
                const ERROR_CODE = JSON.parse(err.message);
                switch (ERROR_CODE.error)
                {
                    case ERROR.INVALID_POST_DATA:
                        response.error = "Invalid post data";
                        break;
                    default:
                        response.error = "General Error";
                        break;
                }
            }
            catch (e)
            {
                response.error = "Error parsing error message";
            }

            return of(response);
        }));

        return serviceObj;
    }
}

export { InspectionService }
